import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useAppStore } from '../store/useAppStore';
import { Colors, SIZES } from '../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import Button3D from '../components/Button3D';
import ScreenBackground from '../components/ScreenBackground';
// MISMO MOTIVO QUE EN kyc-documents.tsx: el `SafeAreaView` de react-native es
// «available only on iOS» y en Android se dibuja como un `<View>` pelado. Con
// `edgeToEdgeEnabled=true` (android/gradle.properties) el unico aire que tenia
// esta pantalla eran los 40 px de `header.marginTop`, medidos desde el borde
// fisico del cristal: menos que una barra de estado con muesca. `PantallaSegura`
// lee el margen real con `useSafeAreaInsets()`.
import PantallaSegura from '../components/PantallaSegura';
import { KYC_WALL_ENFORCED } from '../constants/featureFlags';
import {
  verificacionAplazadaEnEstaSesion,
  aplazarVerificacionEnEstaSesion,
} from './_layout';

/**
 * UNA SESION, UN RECORDATORIO — y aqui es donde mas falta hacia.
 *
 * `_layout.tsx:876` e `index.tsx` ya respetaban el aplazamiento; esta pantalla
 * no lo miraba. Y es la que importa, porque «Cambiar de rol» del embudo no
 * devuelve a la portada: devuelve AQUI (kyc-welcome.tsx:140). Asi que volver a
 * tocar el mismo rol metia a la persona en el embudo otra vez, sin fin.
 *
 * Devuelve true si hay que enseñar el embudo. Marca el recordatorio ANTES de
 * que el llamador navegue, por el mismo motivo que `_layout.tsx:877`: salir del
 * embudo pasa por una ruta de entrada y el guardian volveria a correr.
 */
function tocaRecordarLaVerificacion(): boolean {
  if (verificacionAplazadaEnEstaSesion()) return false;
  aplazarVerificacionEnEstaSesion();
  return true;
}

export default function RoleSelectionScreen() {
  const router = useRouter();
  const setRole = useAppStore((state) => state.setRole);
  const setBaseRole = useAppStore((state) => state.setBaseRole);

  const isVerified = useAppStore((state) => state.isVerified);
  const isDriverVerified = useAppStore((state) => state.isDriverVerified);

  // ══════════════════════════════════════════════════════════════════════
  // EL PASAJERO ENTRA. PUNTO.
  //
  // Antes, elegir "Quiero Viajar" sin estar verificado mandaba a /kyc-welcome
  // —un embudo sin salida— en vez de al mapa. El usuario que solo quería pedir
  // un viaje se topaba con "fotografía tu cédula" y ahí se acababa la app.
  //
  // Ya no hace falta: la regla de Firestore que creaba viajes DEJÓ de exigir
  // `isKycVerified()` para el pasajero a propósito (ver firestore.rules,
  // /rides allow create) — a quien hay que verificar es al chofer, que es
  // quien se lleva a otra persona en su carro. Y el "Muro de Seguridad" del
  // mapa ya respeta KYC_WALL_ENFORCED.
  //
  // Así que con la bandera apagada el pasajero va al mapa y pide su viaje. Si
  // algún día se vuelve a encender, esta función vuelve a pasar por el embudo
  // sin tocar nada más.
  // ══════════════════════════════════════════════════════════════════════
  const entrarA = (destino: string) => {
    if (KYC_WALL_ENFORCED && !isVerified) {
      router.replace('/kyc-welcome');
      return;
    }
    router.replace(destino as any);
  };

  const handleSelectPassenger = () => {
    setRole('PASSENGER');
    setBaseRole('PASSENGER');
    entrarA('/(tabs)/map');
  };

  const handleSelectDriver = () => {
    setRole('DRIVER');
    setBaseRole('DRIVER');
    // ══════════════════════════════════════════════════════════════════════
    // AQUI SE CERRABA EL BUCLE
    // ══════════════════════════════════════════════════════════════════════
    // Esto mandaba SIEMPRE a /kyc-welcome, sin mirar si la persona ya estaba
    // aprobada — a diferencia de «Mi Rent Car» y «Busco Vehiculo», que si lo
    // miran (mas abajo en este mismo archivo).
    //
    // El recorrido completo que describio el fundador: desde el perfil cae en
    // el embudo, la unica salida le borra el rol y lo deja AQUI, y al volver a
    // tocar «Quiero Conducir» esta linea lo devolvia al embudo. Daba vueltas.
    //
    // El chofer habilitado va a su mapa. Al que le faltan los papeles del
    // vehiculo se le sigue enseñando el embudo, que es su sitio.
    if (isDriverVerified) {
      router.replace('/(tabs)/map');
      return;
    }
    // Y si en esta sesion ya se le recordo y dijo «Ahora no», no se le vuelve a
    // poner delante: entra al mapa y el candado de «Conectarse» (map.tsx:6509,
    // mas `isVerifiedDriver()` en las reglas) le dice exactamente que le falta
    // cuando intente trabajar. Repetirle el embudo en cada toque era el bucle.
    if (!tocaRecordarLaVerificacion()) {
      router.replace('/(tabs)/map');
      return;
    }
    router.push('/kyc-welcome');
  };

  const handleSelectFoodCustomer = () => {
    // Igual que el pasajero: se le puede recordar la verificación, pero no
    // encerrarlo en ella para ver el catálogo de restaurantes.
    setRole('FOOD_DELIVERY');
    setBaseRole('FOOD_DELIVERY');
    entrarA('/(tabs)/food');
  };

  const handleSelectRestaurantOwner = () => {
    setRole('RESTAURANT_OWNER');
    setBaseRole('RESTAURANT_OWNER');
    router.replace('/restaurant-dashboard');
  };

  const handleSelectRentCar = () => {
    setRole('RENT_CAR_OWNER');
    setBaseRole('RENT_CAR_OWNER');
    // Las reglas de /vehicles y /rent_negotiations SÍ exigen kycCompleted, así
    // que aquí la verificación es una propuesta con sentido: sin ella no podrá
    // publicar un carro ni chatear. Pero al que YA está verificado no se le
    // vuelve a enseñar el embudo (antes se le enseñaba siempre, y desde ahí no
    // tenía cómo llegar a su panel), y el que no lo está sale con "Ahora no".
    if (isVerified || !tocaRecordarLaVerificacion()) {
      router.replace('/(tabs)/dashboard');
      return;
    }
    router.replace('/kyc-welcome');
  };

  const handleSelectCarRenter = () => {
    setRole('CAR_RENTER');
    setBaseRole('CAR_RENTER');
    // Mismo caso: la regla del chat de negociación exige que AMBAS partes
    // estén verificadas para escribir, así que se le propone — pero el
    // verificado entra directo y el otro puede aplazarlo y mirar el catálogo.
    if (isVerified || !tocaRecordarLaVerificacion()) {
      router.replace('/(tabs)/marketplace');
      return;
    }
    router.replace('/kyc-welcome');
  };

  return (
    <ScreenBackground>
      <PantallaSegura style={styles.safeArea}>
        <View style={styles.container}>
        <View style={styles.header}>
          {/* Salida con TEXTO, no un icono suelto: un chevron sin palabra al
              lado no se lee como "volver" y deja la pantalla con pinta de
              callejón sin salida. */}
          <TouchableOpacity
            onPress={() => (router.canGoBack() ? router.back() : router.replace('/'))}
            style={styles.backButton}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Volver"
          >
            <Ionicons name="arrow-back" size={22} color={Colors.primary} style={{ marginRight: 6 }} />
            <Text style={styles.backButtonText}>Volver</Text>
          </TouchableOpacity>
          <Text style={styles.title}>¿Cómo quieres usar la app hoy?</Text>
          <Text style={styles.subtitle}>Puedes cambiar de modo en cualquier momento desde tu perfil.</Text>
        </View>

        <ScrollView style={styles.cardsContainer} contentContainerStyle={{ paddingBottom: 60 }} showsVerticalScrollIndicator={false}>
          <Text style={styles.sectionLabel}>Elige tu rol para comenzar</Text>
          <Button3D
            title="Quiero Viajar"
            color={Colors.primary}
            icon={<Ionicons name="person" size={24} color="#FFF" />}
            onPress={handleSelectPassenger}
            style={styles.roleButton}
          />
          <Text style={styles.descriptionText}>Pide un taxi o motoconcho. Negocia tu tarifa y viaja seguro.</Text>

          <View style={styles.divider} />

          <Button3D
            title="Pedir Comida"
            color="#FF3B30"
            icon={<Ionicons name="fast-food" size={24} color="#FFF" />}
            onPress={handleSelectFoodCustomer}
            style={styles.roleButton}
          />
          <Text style={styles.descriptionText}>Comida, farmacia, supermercado. Lo que necesites, en tu puerta.</Text>

          <View style={styles.divider} />

          <Button3D
            title="Quiero Conducir"
            color={Colors.accent}
            icon={<Ionicons name="car" size={24} color="#FFF" />}
            onPress={handleSelectDriver}
            style={styles.roleButton}
          />
          {/* El transporte escolar se anuló el 5-oct-2026 (decisión del
              fundador). El texto ya no lo menciona y la Política de Privacidad
              lo dice con todas las letras. */}
          <Text style={styles.descriptionText}>
            Taxi, motor, minibús o delivery. 0% de comisión — el 100% es para ti.
          </Text>

          <View style={styles.divider} />

          <Button3D
            title="Tengo un Negocio"
            color="#FF9500"
            // Cubiertos en la puerta de CUALQUIER negocio dicen lo mismo que
            // decia el rotulo viejo: una tienda, no un plato.
            icon={<Ionicons name="storefront" size={24} color="#FFF" />}
            onPress={handleSelectRestaurantOwner}
            style={styles.roleButton}
          />
          {/* Misma razón que en index.tsx: esta es la puerta de cualquier
              negocio —restaurante, farmacia, supermercado— y el rótulo
              nombraba solo a uno. */}
          {/* Misma corrección que en index.tsx: «te llevas el 100%» se leía como
              «no te cuesta nada», y el envío lo paga el negocio al mensajero.
              Lo que no cobramos es comisión, que es lo que de verdad lo
              distingue de las otras apps. */}
          <Text style={styles.descriptionText}>Restaurante, farmacia, supermercado o cualquier otro. No te cobramos comisión por venta ni cuota mensual: el envío lo arreglas tú con el mensajero.</Text>

          <View style={styles.divider} />

          <Button3D
            title="Mi Rent Car (Dueño)"
            color={Colors.primary} 
            icon={<Ionicons name="briefcase" size={24} color="#FFF" />}
            onPress={handleSelectRentCar}
            style={styles.roleButton}
          />
          <Text style={styles.descriptionText}>Gestiona tu flota y choferes de manera automatizada y segura.</Text>

          <View style={styles.divider} />
          
          <Button3D 
            title="Busco Vehículo" 
            color={Colors.accent} 
            icon={<Ionicons name="search" size={24} color="#FFF" />}
            onPress={handleSelectCarRenter}
            style={styles.roleButton}
          />
          <Text style={styles.descriptionText}>Encuentra el vehículo perfecto para trabajar y alquílalo seguro.</Text>

          {/* Aquí estaba «Soy Director(a) de Escuela». Retirado el 5-oct-2026
              con el resto del perfil escolar. Este selector vive duplicado con
              el de la portada: arreglar solo uno hacía que la directora
              reapareciera por aquí en cuanto alguien tocara «Cambiar de rol». */}
        </ScrollView>
        </View>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
    padding: SIZES.padding,
    backgroundColor: 'rgba(255, 255, 255, 0.85)', // Semi-transparente para que se vea el fondo
  },
  header: {
    // 20 en los dos sistemas: el margen de la barra de estado ya lo pone
    // `PantallaSegura`. Los 40 de Android eran un parche para un margen que no
    // se estaba midiendo; sumarlos al inset real hundiria la cabecera.
    marginTop: 20,
    marginBottom: 20,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginLeft: -4,
    marginBottom: 10,
    borderRadius: 20,
    alignSelf: 'flex-start',
    backgroundColor: 'rgba(33, 150, 243, 0.08)',
  },
  backButtonText: {
    color: Colors.primary,
    fontSize: 14,
    fontWeight: '600',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: SIZES.medium,
    color: Colors.textSecondary,
    lineHeight: 22,
  },
  cardsContainer: {
    flex: 1,
    marginTop: 20,
  },
  sectionLabel: {
    fontSize: SIZES.font,
    color: Colors.textSecondary,
    fontWeight: '600',
    marginBottom: 20,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  roleButton: {
    height: 70,
  },
  descriptionText: {
    fontSize: 14,
    color: Colors.textSecondary,
    marginTop: 10,
    marginBottom: 30,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  divider: {
    height: 1,
    backgroundColor: Colors.border,
    marginBottom: 30,
    marginHorizontal: 40,
  }
});

